/**
 * Оновлення в реальному часі (SSE `/events`, S8 + S9): «повідомити, потім перечитати». Подія
 * несе лише тип, сутність і id — дані клієнт перечитує через API зі своїми правами (SEC-043).
 * Відлуння власної зміни (changeId з відповіді запису) не показуємо як зміну колеги.
 */
import type { RealtimeEvent } from '@clientline/contracts';
import {
  MAX_WATCHED_RECORDS,
  refKey,
  STREAM_WATCH_SUFFIX,
  type StreamHello,
  type WatchedRecord,
} from '@clientline/contracts/stream-watch';
import { type Query, type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { api, ownChanges, platformKeys } from './api.ts';
import { reportNetworkFailure } from './network.ts';
import { streamRefused } from './relogin.ts';
import { hasSessionHint } from './session.ts';
import type { StreamKind, StreamShare } from './stream-share.ts';

type Listener = (event: RealtimeEvent, own: boolean) => void;
const listeners = new Set<Listener>();

type StreamShareModule = typeof import('./stream-share.ts');
let loadShareModule = (): Promise<StreamShareModule> => import('./stream-share.ts');
let shareModule: Promise<StreamShareModule> | null = null;

/**
 * Модуль вкладки-лідера — окремий чанк `stream-share` (бюджет першого екрана, рев'ю #1227). Оболонка
 * починає вантажити його разом із `/me` (`AppShell`), старт потоку чекає той самий проміс. Збій
 * скидає проміс: наступна спроба вантажить знову (S9). Браузер, що запам'ятав відмову модуля, не
 * вилікує й повтор — тоді банер версії (`vite:preloadError`).
 */
export function preloadStreamShare(): Promise<StreamShareModule> {
  shareModule ??= loadShareModule().catch((error: unknown) => {
    shareModule = null;
    throw error;
  });
  return shareModule;
}

/** Почати вантажити чанк лідера, щойно потік знадобиться: паралельно з `/me`, а не після нього. */
export function usePreloadStreamShare(when: boolean): void {
  useEffect(() => {
    if (when) preloadStreamShare().catch(() => undefined);
  }, [when]);
}

/** Тести: підмінити завантаження чанка (збій і повтор). */
export function setStreamShareLoaderForTests(loader?: () => Promise<StreamShareModule>): void {
  loadShareModule = loader ?? (() => import('./stream-share.ts'));
  shareModule = null;
}

/** Відступ повтору, якщо чанк лідера не завантажився: 1 с, 2 с, … до 30 с. */
const SHARE_RETRY_MAX_MS = 30_000;

/** Підписка екрана на події (картка показує «Змінено колегою»). */
export function onRecordEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Події сповіщень `notification.*` (notifications.md, розд. 6): без запису, лише адресату, з
 * плоскими `data`. Їх слухає центр сповіщень (`features/notifications/live.ts`): лічильник, тост.
 */
type NotificationListener = (event: RealtimeEvent) => void;
const notificationListeners = new Set<NotificationListener>();

export function onNotificationEvent(listener: NotificationListener): () => void {
  notificationListeners.add(listener);
  return () => notificationListeners.delete(listener);
}

/**
 * Вікно вхідного дзвінка `calls.ringing` (CALL-10): лише адресату, з плоскими `data`, без запису —
 * дані вікно читає окремим запитом (`features/call/incoming/`).
 */
const INCOMING_CALL_EVENT = 'calls.ringing';
const incomingCallListeners = new Set<NotificationListener>();

export function onIncomingCallEvent(listener: NotificationListener): () => void {
  incomingCallListeners.add(listener);
  return () => incomingCallListeners.delete(listener);
}

/**
 * Списки сутності, які на екрані оновлює живе оновлення (LST-18, `list-live.ts`): подія лише
 * позначає їх застарілими, а не перечитує всі порції — рядки не переїжджають під людиною. Решта
 * списків сутності (кеш інших подань) перечитується при наступному показі, як і раніше.
 */
const liveLists = new Map<string, number>();

export function holdListRefresh(entity: string): () => void {
  liveLists.set(entity, (liveLists.get(entity) ?? 0) + 1);
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    const n = (liveLists.get(entity) ?? 1) - 1;
    if (n > 0) liveLists.set(entity, n);
    else liveLists.delete(entity);
  };
}

/**
 * Чи живий потік подій (LST-18): `false` — немає з'єднання чи обрив, список переходить на
 * опитування раз на 60 с. Підписка — для живого оновлення списку.
 */
let streamOpen = false;
const streamListeners = new Set<() => void>();
export const isStreamOpen = () => streamOpen;
export function onStreamState(listener: () => void): () => void {
  streamListeners.add(listener);
  return () => streamListeners.delete(listener);
}
function setStreamOpen(open: boolean) {
  if (open === streamOpen) return;
  streamOpen = open;
  for (const listener of streamListeners) listener();
}

/** Потік має бути: людина ввійшла, режим його хоче (`usePlatformEvents` працює). */
let streamWanted = false;
export const isStreamWanted = () => streamWanted;
function setStreamWanted(wanted: boolean) {
  if (wanted === streamWanted) return;
  streamWanted = wanted;
  for (const listener of streamListeners) listener();
}

/**
 * Відлуння власної зміни (аудит № 22): автор зміни сам оновив те, що на екрані (`setQueryData` з
 * відповіді, власна інвалідація), а подія приходить за ним за десятки мілісекунд. Друге
 * перечитування не отримує запит, що отримав дані щойно (за це вікно), і запит у дорозі, який
 * почався вже після відповіді зміни (його запустив сам автор, він читає зроблене). Запит у дорозі,
 * що почався раніше (чи невідомо коли), перезапускається: він міг читати до коміту зміни (дві
 * швидкі правки поспіль). Решта ключів запису — як для чужої зміни: автор міг оновити не все
 * (лінія запису, пов'язане). Списки сутності подія позначає застарілими завжди: їх перечитує показ
 * або живе оновлення, а не ця подія.
 */
export const OWN_ECHO_FRESH_MS = 2_000;

/**
 * Коли почалося поточне читання запиту (`performance.now()`, як мітки `ownChanges`); стежить
 * `trackFetchStarts`. Перезапуск читання через `cancelRefetch` дію `fetch` не завжди надсилає: тоді
 * лишається мітка попереднього, ранішого початку — і наступне відлуння перезапустить його знову.
 * Це обережний бік: зайве читання, а не пропущена зміна.
 */
const fetchStarts = new WeakMap<Query, number>();

/**
 * Стежити, коли починаються читання кешу `client` (App.tsx): відлуння відрізняє читання, яке автор
 * запустив після своєї відповіді, від того, що почалося раніше. Повертає відписку.
 */
export function trackFetchStarts(client: QueryClient): () => void {
  return client.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'fetch')
      fetchStarts.set(event.query, performance.now());
  });
}

const ownEchoStale = (now: number, ownAt: number) => (query: Query) => {
  if (query.state.fetchStatus === 'fetching') {
    const started = fetchStarts.get(query);
    return started === undefined || started < ownAt;
  }
  return now - query.state.dataUpdatedAt > OWN_ECHO_FRESH_MS;
};

const isMeQuery = (query: Query) =>
  query.queryKey.length === platformKeys.me.length &&
  platformKeys.me.every((part, i) => query.queryKey[i] === part);

/**
 * Читання, що почалося до підписки потоку (`ready` нового з'єднання; #1137). Нове з'єднання без
 * `Last-Event-ID` сервер веде від поточного `seq`: подія між таким читанням і підпискою загубилась
 * би, і екран лишився б застарілим до наступної події чи кінця `staleTime`. Невідомий початок
 * читання з даними — теж (обережний бік: зайве читання, а не пропущена зміна).
 */
export const readBeforeStream = (readyAt: number) => (query: Query) => {
  // Хто ввійшов (`/me`) — не дані потоку: жодна подія його не скидає, а перечитування під уже
  // чужою cookie тихо прийняло б іншу людину (рев'ю #1227: Архітектор S7а, Security). Лише цей
  // ключ: `['p', 'meta']` з правами інтерфейсу перечитується, як і було.
  if (isMeQuery(query)) return false;
  const started = fetchStarts.get(query);
  return started === undefined ? query.state.dataUpdatedAt > 0 : started < readyAt;
};

/** Подія потоку → що перечитати (експорт — для тестів). */
export function applyEvent(client: QueryClient, event: RealtimeEvent) {
  if (event.type === 'schema.changed') {
    void client.invalidateQueries({ queryKey: platformKeys.meta });
    // Поля чи вид довідника могли змінитись (references.md, розд. 4, п. 3).
    void client.invalidateQueries({ queryKey: platformKeys.references });
    return;
  }
  if (event.type.startsWith('notification.')) {
    for (const listener of notificationListeners) listener(event);
    return;
  }
  if (event.type === INCOMING_CALL_EVENT) {
    for (const listener of incomingCallListeners) listener(event);
    return;
  }
  // Сесія скриньки щось змінила: папки, список і лічильник у навігації — запитами з правами людини.
  if (event.type === 'mail.mailbox_changed') {
    void client.invalidateQueries({ queryKey: ['mail'] });
    return;
  }
  // Будь-яка подія з сутністю довідника скидає його кеш, зокрема `reference.changed` — сигнал без
  // id про запис, якого людина вже не бачить (кошик, вихід з області; references.md, розд. 4).
  // Кеш є лише у відкритого довідника: для інших сутностей це порожня операція.
  if (event.entity)
    void client.invalidateQueries({ queryKey: platformKeys.reference(event.entity) });
  if (!event.entity || !event.recordId) return;
  const own = ownChanges.has(event.id);
  const predicate = own
    ? {
        predicate: ownEchoStale(Date.now(), ownChanges.at(event.id) ?? Number.POSITIVE_INFINITY),
      }
    : {};
  void client.invalidateQueries({
    queryKey: platformKeys.lists(event.entity),
    ...(liveLists.has(event.entity) ? { refetchType: 'none' as const } : {}),
  });
  const recordKeys = [
    platformKeys.record(event.entity, event.recordId),
    platformKeys.history(event.entity, event.recordId),
    // Панелі пов'язаного запису (REC-12): записи дочірніх сутностей змінюються разом із ним.
    platformKeys.related(event.entity, event.recordId),
    // Лінія запису: `timeline.changed` (предмети й події піддерева) і власні зміни запису (аудит).
    platformKeys.timeline(event.entity, event.recordId),
  ];
  for (const queryKey of recordKeys) void client.invalidateQueries({ queryKey, ...predicate });
  for (const listener of listeners) listener(event, own);
}

/**
 * Перечитати все видиме після пропущених подій: записи платформи (`['p']`) і кабінет пошти
 * (`['mail']`, сигнал `mail.mailbox_changed` — рев'ю ML-W1, Архітектор S2).
 */
function invalidateVisible(client: QueryClient) {
  void client.invalidateQueries({ queryKey: ['p'] });
  void client.invalidateQueries({ queryKey: ['mail'] });
}

/**
 * Відкриті картки вкладки (AG-69, п. б; #1065; contracts `stream-watch`): сервер повідомляє
 * `record.unavailable` лише про записи з цього переліку, і лише тим, хто їх бачив. Перелік іде цілком
 * на кожну зміну й на кожне з'єднання (`stream` приходить і після перепідключення). З'єднання одне на
 * браузер (UX-24): перелік надсилає вкладка-лідер, зібравши відкриті картки всіх вкладок
 * (`stream-share.ts`). Відмова (`404` — з'єднання вже немає чи воно в іншому процесі) нічого не
 * ламає: картку далі перевіряє такт `record-gone.ts`.
 */
const watched = new Map<string, WatchedRecord & { count: number }>();
let streamId: string | null = null;
let watchTimer: ReturnType<typeof setTimeout> | null = null;
/** Потік вкладки (лідер чи послідовник), поки він є. */
let share: StreamShare | null = null;
/** Кілька карток монтуються разом (`J` / `K`, бічна панель): один запит на всіх. */
const WATCH_DEBOUNCE_MS = 50;

/** Відкриті картки цієї вкладки, найновіші, до межі переліку. */
const ownRecords = (): WatchedRecord[] =>
  [...watched.values()].slice(-MAX_WATCHED_RECORDS).map(({ entity, id }) => ({ entity, id }));

function sendWatch() {
  watchTimer = null;
  const stream = streamId;
  if (!stream) return;
  const records = share?.records() ?? ownRecords();
  // `read`: службовий запит, не зміна людини — без таймера «зайнято» і без лічби незбереженого.
  void api<void>('PUT', `/events${STREAM_WATCH_SUFFIX}`, {
    body: { stream, records },
    read: true,
  }).catch(() => undefined);
}

function scheduleWatch() {
  if (!streamId || watchTimer || !share?.isLeader()) return;
  watchTimer = setTimeout(sendWatch, WATCH_DEBOUNCE_MS);
}

/** Картка відкрита: запис у переліку, поки не викликано повернену функцію. */
export function watchRecord(entity: string, id: string): () => void {
  const key = refKey({ entity, id });
  const entry = watched.get(key);
  if (entry) entry.count += 1;
  else {
    watched.set(key, { entity, id: id.toLowerCase(), count: 1 });
    share?.ownChanged();
  }
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    const current = watched.get(key);
    if (!current) return;
    current.count -= 1;
    if (current.count > 0) return;
    watched.delete(key);
    share?.ownChanged();
  };
}

/**
 * Запис з переліку вийшов з видимості: той самий шлях, що й такт запасного шляху, — перевірка
 * відкритої картки зараз (умовний `GET`); `403` / `404` прибирає запис з усього кешу вкладки
 * (`record-gone.ts`). Подія не несе жодного поля, тож сама нічого не показує.
 */
export function applyUnavailable(client: QueryClient, record: WatchedRecord) {
  const id = record.id.toLowerCase();
  void client.refetchQueries({
    queryKey: platformKeys.recordCheck(record.entity, id).slice(0, 3),
    // Сервер шле id малими літерами, адреса картки могла прийти й великими.
    predicate: ({ queryKey }) => String(queryKey[3]).toLowerCase() === id,
  });
}

/** Стан потоку для компонента: перемальовує, коли потік відкрився чи обірвався. */
export const useStreamOpen = () => useSyncExternalStore(onStreamState, isStreamOpen, () => false);

/**
 * Потік подій, поки людина ввійшла: одне з'єднання на браузер, решта вкладок отримують його від
 * вкладки-лідера (UX-24, `stream-share.ts`). `user` — хто ввійшов (`GET /me`): без нього потоку
 * немає, бо канал вкладок і звірка потоку — за людиною. Обрив — EventSource перепідключається сам.
 *
 * Кеш «назад / вперед» (bfcache, UX-34): відкрите з'єднання може не пустити сторінку в кеш, а
 * події, що прийшли б, поки вона там, губляться. Тому на `pagehide` потік закриваємо (лідер віддає
 * замок іншій вкладці, послідовник знімає свої картки з переліку лідера), а коли сторінка повернулась із кешу (`pageshow` з
 * `persisted`), відкриваємо знову: пропущене за час у кеші інакше не підтягнеться. Записи (`['p']`)
 * перечитує `ready` — нового з'єднання чи, якщо вкладка стала послідовником, від лідера (`state`):
 * усе прочитане до нього, зокрема неактивне; явне скидання тут дало б те саме читання двічі
 * (#1163). Кабінет пошти `ready` не покриває — його скидаємо тут.
 * Якщо за час у кеші людина вийшла (ознаки входу немає), сторінку веде на вхід `watchSignOut`
 * (sign-out.ts): тоді ні з'єднання, ні запитів — вони дали б лише 401.
 */
export function usePlatformEvents(enabled: boolean, user: string | undefined) {
  const client = useQueryClient();
  useEffect(() => {
    if (!enabled || !user || typeof EventSource === 'undefined') return;
    // Скільки разів почався потік (`stream`): друге й далі — запис міг зникнути між з'єднаннями, а
    // нове з'єднання бере в перелік лише видиме.
    let streams = 0;
    let opened = false;
    const deliver = (kind: StreamKind, data: string) => {
      switch (kind) {
        case 'open':
          // Перепідключення після обриву чи новий лідер: події між з'єднаннями могли загубитись —
          // довідники перечитати (`If-None-Match`, без зміни — `304`). Перше відкриття — `ready`.
          if (opened) void client.invalidateQueries({ queryKey: platformKeys.references });
          opened = true;
          setStreamOpen(true);
          return;
        case 'error':
          // Обрив потоку — ще один факт для стану мережі (UX-29): чи є зв'язок, скаже перевірка.
          streamId = null;
          setStreamOpen(false);
          reportNetworkFailure();
          return;
        case 'closed':
          streamId = null;
          setStreamOpen(false);
          return;
        case 'message':
          try {
            applyEvent(client, JSON.parse(data) as RealtimeEvent);
          } catch {
            // Пошкоджене повідомлення — пропускаємо, наступне перечитування все одно підтягне стан.
          }
          return;
        case 'ready':
          // Нове з'єднання підписане: те, що прочитано до цього, могло пропустити подію (#1137).
          // `ready` приходить лише без `Last-Event-ID`; перепідключення добирає пропущене саме.
          void client.invalidateQueries({
            queryKey: ['p'],
            predicate: readBeforeStream(performance.now()),
          });
          return;
        case 'reset':
          // Пропущено забагато або фільтр не відпрацював (fail closed) — перечитати все видиме.
          invalidateVisible(client);
          return;
        case 'stream':
          // Id з'єднання — на кожне (пере)підключення: перелік відкритих карток іде на нове
          // з'єднання. Надсилає його лише лідер.
          try {
            const hello = JSON.parse(data) as StreamHello;
            if (share?.isLeader()) {
              streamId = hello.stream;
              if (share.records().length > 0) scheduleWatch();
            }
          } catch {
            // Без id перелік не йде: лишається такт перевірки картки.
          }
          // Пропущене між з'єднаннями — перевірити відкриті картки зараз, а не через такт.
          if (streams++ > 0)
            for (const record of watched.values()) applyUnavailable(client, record);
          return;
        case 'unavailable':
          try {
            applyUnavailable(client, JSON.parse(data) as WatchedRecord);
          } catch {
            // Пошкоджене повідомлення — картку перевірить такт.
          }
          return;
        case 'foreign':
          // Сервер віддав потік іншої людини: у браузері ввійшов інший. Вміст схований до звірки `/me`:
          // інша людина чи збій звірки — повний перехід на старт (умова 4, fail closed).
          void streamRefused(client);
          return;
      }
    };
    // Модуль лідера — окремий чанк (`preloadStreamShare`). `generation` відкидає завантаження, яке
    // пережило `stop()`. Збій чанка — повтор з відступом, на `online` чи `pageshow` (рев'ю #1227, S9);
    // до того потоку немає, і за 30 с з'явиться «Оновлення призупинено».
    let generation = 0;
    let attempt = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const retryNow = () => {
      if (retry === undefined) return;
      clearTimeout(retry);
      retry = undefined;
      if (!share && hasSessionHint()) start();
    };
    const start = () => {
      setStreamWanted(true);
      const current = ++generation;
      preloadStreamShare().then(
        ({ startStreamShare }) => {
          if (current !== generation || share) return;
          attempt = 0;
          share = startStreamShare({ user, deliver, ownRecords, watchChanged: scheduleWatch });
        },
        () => {
          if (current !== generation) return;
          retry = setTimeout(retryNow, Math.min(SHARE_RETRY_MAX_MS, 1000 * 2 ** attempt++));
        },
      );
    };
    const stop = () => {
      generation++;
      clearTimeout(retry);
      retry = undefined;
      share?.stop();
      share = null;
      streamId = null;
      setStreamOpen(false);
    };
    // Сторінка йде (закрита, перехід чи кеш «назад / вперед»): лідер віддає замок, послідовник —
    // знімає свої картки з переліку лідера (рев'ю Архітектора #1227, S1). Потік поки не потрібен,
    // тож «Оновлення призупинено» не блимне після повернення (N5).
    const onPageHide = () => {
      stop();
      setStreamWanted(false);
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || share || !hasSessionHint()) return;
      start();
      void client.invalidateQueries({ queryKey: ['mail'] });
    };
    start();
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('online', retryNow);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('online', retryNow);
      stop();
      setStreamWanted(false);
    };
  }, [enabled, user, client]);
}
